import type { ThinkingStep } from '@Models';
import { appendThinkingStep } from '../../Helpers/Agents/thinkingSteps';
import type { StreamEvent } from './streamingHelpers';

/**
 * The model's answer usually reaches the stream as one burst, so it is revealed
 * progressively, but every burst is fully on screen within REVEAL_MAX_MS.
 */
export const REVEAL_MAX_MS = 1500;
const REVEAL_TICK_MS = 30;
const REVEAL_MIN_CHARS = 4;

/** Events this module does not interpret; the caller maps them onto the UI. */
export type AgentTurnPassthroughEvent = Exclude<
  StreamEvent,
  { type: 'chunk' } | { type: 'trace' } | { type: 'progress' } | { type: 'retract' } | { type: 'error' }
>;

export interface AgentTurnHandlers {
  /** Answer text to show so far (throttled while streaming, exact on completion). */
  onAnswer: (text: string) => void;
  onThinkingChange: (isThinking: boolean) => void;
  onStep: (step: ThinkingStep) => void;
  onEvent?: (event: AgentTurnPassthroughEvent) => void;
  /** The answer so far was narration: drop anything derived from it. */
  onRetract?: () => void;
}

export interface AgentTurnOptions {
  startedAtMs: number;
  now?: () => number;
  /** Aborting stops the reveal at once: nothing renders afterwards and the turn rejects with an AbortError. */
  signal?: AbortSignal;
}

export interface AgentTurnResult {
  answer: string;
  steps: ThinkingStep[];
  /** Time from the request to the first answer token (or to the end, if none came). */
  thinkingMs: number;
  receivedDone: boolean;
}

const TABLE_LINE = /^\s*\|/;

/**
 * Hides a GFM table row that is still being written, and a header row whose
 * separator has not arrived yet, so a streaming table never flashes as raw pipes.
 */
export const trimIncompleteTableTail = (text: string): string => {
  let end = text.length;
  const lastNl = text.lastIndexOf('\n');
  if (TABLE_LINE.test(text.slice(lastNl + 1))) end = lastNl + 1;

  const body = text.slice(0, end);
  const lines = body.endsWith('\n') ? body.slice(0, -1) : body;
  const prevNl = lines.lastIndexOf('\n');
  const lastLine = lines.slice(prevNl + 1);
  if (lastLine && TABLE_LINE.test(lastLine)) {
    const before = prevNl === -1 ? '' : lines.slice(0, prevNl);
    const lineBefore = before.slice(before.lastIndexOf('\n') + 1);
    if (prevNl === -1 || !TABLE_LINE.test(lineBefore)) end = prevNl + 1;
  }
  return text.slice(0, end);
};

/**
 * Consumes one agent turn from the NDJSON stream: records every visible step,
 * renders answer chunks as they arrive, and handles `retract` by discarding the
 * answer so far and returning to the thinking state.
 */
export async function consumeAgentTurn(
  stream: AsyncGenerator<StreamEvent, void, undefined>,
  handlers: AgentTurnHandlers,
  { startedAtMs, now = Date.now, signal }: AgentTurnOptions,
): Promise<AgentTurnResult> {
  let answer = '';
  let rendered: string | null = null;
  let isThinking = true;
  let thinkingMs: number | null = null;
  let steps: ThinkingStep[] = [];
  let receivedDone = false;
  let timer: ReturnType<typeof setTimeout> | null = null;
  let shownChars = 0;
  let revealDeadline = 0;
  let onCaughtUp: (() => void) | null = null;
  let aborted = false;

  const elapsed = () => Math.max(0, now() - startedAtMs);

  const clearTimer = () => {
    if (timer !== null) clearTimeout(timer);
    timer = null;
  };

  const releaseReveal = () => {
    const resolve = onCaughtUp;
    onCaughtUp = null;
    resolve?.();
  };

  const onAbort = () => {
    aborted = true;
    clearTimer();
    releaseReveal();
  };

  const render = (text: string) => {
    if (aborted || text === rendered) return;
    rendered = text;
    handlers.onAnswer(text);
  };

  const revealTick = () => {
    timer = null;
    const backlog = answer.length - shownChars;
    if (backlog > 0) {
      const hidden = typeof document !== 'undefined' && document.visibilityState === 'hidden';
      const remainingMs = Math.max(REVEAL_TICK_MS, revealDeadline - now());
      const step = hidden ? backlog : Math.max(REVEAL_MIN_CHARS, Math.ceil((backlog * REVEAL_TICK_MS) / remainingMs));
      // Snap to the end of a word so text appears word by word.
      const nextSpace = answer.slice(shownChars + step).search(/\s/);
      shownChars = nextSpace < 0 ? answer.length : shownChars + step + nextSpace;
      render(trimIncompleteTableTail(answer.slice(0, shownChars)));
    }
    if (shownChars < answer.length) {
      timer = setTimeout(revealTick, REVEAL_TICK_MS);
    } else {
      releaseReveal();
    }
  };

  // New text gets at most REVEAL_MAX_MS to be fully on screen.
  const revealMore = () => {
    if (aborted) return;
    revealDeadline = now() + REVEAL_MAX_MS;
    if (timer === null) revealTick();
  };

  const waitForReveal = () =>
    aborted || shownChars >= answer.length
      ? Promise.resolve()
      : new Promise<void>((resolve) => {
          onCaughtUp = resolve;
        });

  const recordStep = (step: ThinkingStep) => {
    const next = appendThinkingStep(steps, step);
    if (next === steps) return;
    steps = next;
    handlers.onStep(step);
  };

  const abortError = () => new DOMException('aborted', 'AbortError');

  if (signal?.aborted) throw abortError();
  signal?.addEventListener('abort', onAbort, { once: true });

  try {
    try {
      // eslint-disable-next-line no-constant-condition
      while (true) {
        // eslint-disable-next-line no-await-in-loop
        const { done, value: event } = await stream.next();
        if (done || aborted) break;

        if (event.type === 'chunk') {
          if (event.text) {
            answer += event.text;
            if (isThinking) {
              isThinking = false;
              thinkingMs = elapsed();
              handlers.onThinkingChange(false);
            }
            revealMore();
          }
        } else if (event.type === 'progress') {
          const label = event.text?.trim() ?? '';
          if (label) recordStep({ label, tMs: elapsed() });
        } else if (event.type === 'retract') {
          clearTimer();
          answer = '';
          shownChars = 0;
          render('');
          handlers.onRetract?.();
          if (!isThinking) {
            isThinking = true;
            thinkingMs = null;
            handlers.onThinkingChange(true);
          }
        } else if (event.type !== 'error' && event.type !== 'trace') {
          // Tool traces are not shown: the panel holds only the model's own notes.
          handlers.onEvent?.(event);
          if (event.type === 'done') {
            receivedDone = true;
            // eslint-disable-next-line no-await-in-loop
            await stream.return(undefined);
            break;
          }
        }
      }
    } catch (streamError) {
      // A connection closing after `done` is a normal end of stream.
      if (aborted) throw abortError();
      if (!(receivedDone && answer)) throw streamError;
    }

    if (answer) {
      await waitForReveal();
      render(answer);
    }
    if (aborted) throw abortError();
  } finally {
    clearTimer();
    signal?.removeEventListener('abort', onAbort);
  }

  return { answer, steps, thinkingMs: thinkingMs ?? elapsed(), receivedDone };
}
